import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import {
  MsalAuthenticationTemplate,
  useIsAuthenticated,
  InteractionType,
} from 'src/auth/firebaseMsalCompat';

import { Footer } from '../components/Footer';
import { UserAvatar } from '../components/UserAvatar';
import { SubscriptionStatusCard } from '../components/SubscriptionStatusCard';
import { ApiKeyCard } from '../components/ApiKeyCard';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { StatusUnavailable } from '../components/StatusUnavailable';
import { auth } from '../auth/firebase';
import { getDisplayName, validateDisplayName } from '../auth/displayName';
import { useFirebaseAuth } from '../auth/FirebaseAuthContext';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useUserProfile } from '../hooks/useUserProfile';
import { useAuth } from '../components/NavBar/useAuth';
import {
  invalidateUserProfile,
  profileErrorMessage,
  setCachedUserProfile,
  updateDisplayName,
} from '../services/userProfile';
import { trackPageView, trackButtonClick } from '../services/telemetry';

function Profile() {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useFirebaseAuth();
  const { profile, error: profileError, eligible, refresh } = useUserProfile();
  const { handleLogout } = useAuth();
  const { subscriptionStatus, entitlement, retryStatus } =
    useSubscriptionStatus(isAuthenticated);

  const renameInputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  const profilePending = eligible && !profile;
  const displayName = getDisplayName({
    profileName: profile?.name ?? null,
    firebaseUser: user,
    fallback: 'Your account',
    profilePending,
  });
  const email = user?.email ?? '';
  // Rename writes the Mongo row, so it stays hidden until that row has loaded.
  const canRename = !!profile && !editing;

  const startRename = () => {
    setDraft(profile?.name ?? '');
    setRenameError(null);
    setEditing(true);
  };

  const cancelRename = () => {
    setEditing(false);
    setRenameError(null);
  };

  const saveRename = async () => {
    if (saving || !user) return;
    const checked = validateDisplayName(draft);
    if (!checked.ok) {
      setRenameError(checked.error);
      return;
    }
    setSaving(true);
    setRenameError(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not signed in');
      const updated = await updateDisplayName(token, checked.value);
      setCachedUserProfile(user.uid, updated);
      setEditing(false);
      await trackButtonClick('Profile', 'Rename');
    } catch (err: unknown) {
      setRenameError(profileErrorMessage(err));
      // The server may have saved the name before the response was lost.
      invalidateUserProfile();
      try {
        const fresh = await refresh();
        if (fresh && fresh.name === checked.value) {
          setRenameError(null);
          setEditing(false);
          await trackButtonClick('Profile', 'Rename');
        }
      } catch {
        // Leave the inline error. The heading stays blank until a load works.
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (editing) renameInputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    document.title = 'My Profile - PineappleMusicLab';
    trackPageView('Profile', '/profile');
  }, []);

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col">
      <div className="bg-gray-25 flex-grow p-4 font-[Outfit]">
        <div className="mx-auto mt-6 mb-10 flex max-w-2xl flex-col gap-6">
          {/* Identity */}
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <UserAvatar size={112} />
            <div className="w-full min-w-0">
              {editing ? (
                <form
                  className="flex flex-col items-center gap-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void saveRename();
                  }}
                >
                  <input
                    className={`
                      w-full max-w-sm rounded-lg border border-gray-300 bg-white
                      px-4 py-2 text-center text-xl text-gray-800
                      focus:border-gray-400 focus:ring-2
                      focus:ring-yellow-400/40 focus:outline-none
                    `}
                    ref={renameInputRef}
                    value={draft}
                    aria-label="Username"
                    disabled={saving}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelRename();
                      }
                    }}
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className={`
                        cursor-pointer rounded-full bg-[#333] px-4 py-2 text-sm
                        font-semibold text-white
                        hover:bg-black
                        disabled:cursor-not-allowed disabled:opacity-50
                      `}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelRename}
                      disabled={saving}
                      className={`
                        cursor-pointer rounded-full border border-gray-300 px-4
                        py-2 text-sm text-gray-700
                        hover:bg-gray-50
                        disabled:cursor-not-allowed disabled:opacity-50
                      `}
                    >
                      Cancel
                    </button>
                  </div>
                  {renameError && (
                    <p className="text-sm text-red-600" role="alert">
                      {renameError}
                    </p>
                  )}
                </form>
              ) : profilePending && profileError ? (
                <div className="flex flex-col items-center gap-3" role="alert">
                  <p className="text-sm text-gray-600">
                    Couldn&apos;t load your username.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      void refresh().catch(() => {
                        // The failed load already set profileError.
                      });
                    }}
                    className={`
                      cursor-pointer rounded-full border border-gray-800 px-4
                      py-1.5 text-sm font-semibold text-gray-800
                      transition-colors duration-75
                      hover:border-yellow-500 hover:bg-yellow-400
                    `}
                  >
                    Try again
                  </button>
                </div>
              ) : profilePending ? (
                <p className="text-lg text-gray-400">Loading…</p>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <h1
                    className={`
                      max-w-full min-w-0 truncate text-4xl font-bold
                      text-gray-800
                    `}
                    title={displayName}
                    translate="no"
                  >
                    {displayName}
                  </h1>
                  {canRename && (
                    <button
                      type="button"
                      aria-label="Rename"
                      onClick={startRename}
                      className={`
                        cursor-pointer rounded-full p-2 text-gray-400
                        hover:bg-gray-100 hover:text-gray-700
                      `}
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                  )}
                </div>
              )}
              {email && (
                <p translate="no" className="mt-2 text-lg text-gray-500">
                  {email}
                </p>
              )}
            </div>
          </div>

          {/* Subscription. `statusLoading || !subscriptionStatus` used to gate the
              spinner, which meant a 502 or a timeout left this panel spinning
              forever — the user was never told their plan couldn't be read, on the
              one page whose entire purpose is showing it. */}
          {entitlement === 'failed' ? (
            <StatusUnavailable
              message="We couldn't load your plan details."
              onRetry={retryStatus}
              className="p-10"
            />
          ) : entitlement === 'pending' || !subscriptionStatus ? (
            <div
              className={`
                flex w-full items-center justify-center rounded-2xl border
                border-gray-200 bg-white p-10
              `}
            >
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <SubscriptionStatusCard
                subscriptionStatus={subscriptionStatus}
                source="Profile"
              />
              {subscriptionStatus.userType === 'business' && <ApiKeyCard />}
            </>
          )}

          {/* Account actions */}
          <div
            className={`
              w-full overflow-hidden rounded-2xl border border-gray-200 bg-white
              shadow-sm
            `}
          >
            <button
              onClick={async () => {
                await trackButtonClick('Profile', 'Log Out');
                handleLogout();
              }}
              className={`
                flex w-full cursor-pointer items-center gap-3 px-6 py-4
                text-left text-gray-700 transition-colors
                hover:bg-gray-50
              `}
            >
              <FontAwesomeIcon icon={faRightFromBracket} />
              Log Out
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export const ProfileWithMsal = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
    <Profile />
  </MsalAuthenticationTemplate>
);
